/**
 * Ghost Application Info Card Component
 * Displays ghost application basic information
 */

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatDateTime } from '../../../../utils/dateUtils';
import type { GhostApplicationDetail } from '../../../../types/ghostVerification';

interface GhostApplicationInfoCardProps {
  application: GhostApplicationDetail;
}

const getStatusColor = (status: string, theme: any): string => {
  switch (status) {
    case 'Pending':
      return theme.palette.warning.main;
    case 'Approved':
      return theme.palette.success.main;
    case 'Rejected':
      return theme.palette.error.main;
    default:
      return theme.palette.text.secondary;
  }
};

export const GhostApplicationInfoCard: React.FC<GhostApplicationInfoCardProps> = ({
  application,
}) => {
  const theme = useTheme();

  const renderInfoItem = (label: string, value: string | undefined | null) => {
    if (!value) return null;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: theme.spacing(2) }}>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.875rem',
            flex: '0 0 auto',
            marginRight: theme.spacing(2),
          }}
        >
          {label}:
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: theme.palette.text.primary,
            fontSize: '0.875rem',
            textAlign: 'right',
            flex: 1,
          }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <Card>
      <CardContent>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            marginBottom: theme.spacing(2.5),
            color: theme.palette.text.primary,
            fontSize: '1rem',
          }}
        >
          Application Information
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          {/* Ghost ID */}
          {/* {renderInfoItem('Ghost ID', application.ghostId)} */}

          {/* Call Sign */}
          {renderInfoItem('Call Sign', application.applicantName)}

          {/* Status */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(2) }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.875rem',
                flex: '0 0 auto',
                marginRight: theme.spacing(2),
              }}
            >
              Status:
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Chip
                label={application.verificationStatus}
                sx={{
                  backgroundColor: getStatusColor(application.verificationStatus, theme),
                  color: '#FFFFFF',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: 24,
                  borderRadius: theme.shapes.borderRadius.lg,
                }}
              />
            </Box>
          </Box>

          {/* Email */}
          {renderInfoItem('Email', application.email)}

          {/* Phone Number */}
          {renderInfoItem('Phone Number', application.phoneNumber)}

          {/* Submission Date */}
          {renderInfoItem('Submission Date', formatDateTime(application.submittedOn))}

          {/* Reviewed By */}
          {application.reviewedBy && renderInfoItem('Reviewed By', application.reviewedBy)}

          {/* Reviewed At */}
          {application.reviewedAtText && renderInfoItem('Reviewed At', application.reviewedAtText)}
        </Box>
      </CardContent>
    </Card>
  );
};

